import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Button,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { projects } from "../data/projects";

const Projects: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, mb: 4, textAlign: "center" }}
      >
        Projects
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2, 1fr)",
          },
          gap: 3,
        }}
      >
        {projects.map((project, index) => (
          <ProjectCard key={index} project={project} />
        ))}
      </Box>
    </Container>
  );
};

const ProjectCard: React.FC<{ project: (typeof projects)[0] }> = ({
  project,
}) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  React.useEffect(() => {
    if (!isHovered || project.screenshots.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % project.screenshots.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isHovered, project.screenshots.length]);

  const handleImageClick = (direction: "prev" | "next") => {
    if (direction === "next") {
      setCurrentImage((prev) => (prev + 1) % project.screenshots.length);
    } else {
      setCurrentImage(
        (prev) =>
          (prev - 1 + project.screenshots.length) % project.screenshots.length
      );
    }
  };

  return (
    <Card
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      {project.screenshots.length > 0 && (
        <Box sx={{ position: "relative" }}>
          <CardMedia
            component="img"
            height="300"
            image={project.screenshots[currentImage]}
            alt={`${project.title} screenshot ${currentImage + 1}`}
            sx={{ objectFit: "cover" }}
          />
          {project.screenshots.length > 1 && (
            <>
              <IconButton
                onClick={() => handleImageClick("prev")}
                sx={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                  },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={() => handleImageClick("next")}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                  },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 12,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: 0.5,
                }}
              >
                {project.screenshots.map((_, idx) => (
                  <Box
                    key={idx}
                    onClick={() => setCurrentImage(idx)}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor:
                        idx === currentImage
                          ? "primary.main"
                          : "rgba(255, 255, 255, 0.5)",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      )}

      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
          {project.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {project.description}
        </Typography>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
          {project.techStack.map((tech, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: "rgba(145, 189, 58, 0.1)",
              }}
              title={tech.name}
            >
              <Box
                component="img"
                src={tech.icon}
                alt={tech.name}
                sx={{ width: 16, height: 16, objectFit: "contain" }}
              />
              <Typography
                variant="caption"
                sx={{ color: "primary.main", fontWeight: 500 }}
              >
                {tech.name}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "flex", gap: 1, mt: "auto" }}>
          {project.git && (
            <Button
              href={project.git}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              size="small"
            >
              GitHub
            </Button>
          )}
          {project.liveUrl && (
            <Button
              href={project.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              size="small"
            >
              Live Demo
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default Projects;
